
import Vue from 'vue';
import { Route } from 'vue-router/types/router';
import _find from 'lodash/find';
import { mapActions, mapGetters, mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import { APP_BAR_HEIGHT } from '@/components/Toolbar/constants';
import { Session, SessionState, SESSION_STATE } from '@run-diffusion/shared';
import { ToolbarState } from '@/store';
import { AwaitingServerUrlUi200Mixin } from '@/mixins';
import {
	BOOTING_SESSION_STATES,
	READY_SESSION_STATES,
	STOPPING_SESSION_STATES
} from '@/constants/constants';
import SessionInfoCard from '@/components/SessionInfoCard.vue';
import FileBrowserBtn from '@/components/Toolbar/FileBrowserBtn.vue';
import EnabledApiAnimation from '@/components/EnabledApiAnimation.vue';
import NotificationsBtn from '@/components/Toolbar/NotificationsBtn.vue';
import SessionTimer from '@/components/Toolbar/SessionTimer/SessionTimer.vue';
import AdminSessionStats from '@/components/AdminStats/AdminSessionStats.vue';
import FileBrowserNewTabBtn from '@/components/Toolbar/FileBrowserNewTabBtn.vue';
import SessionTimerBtns from '@/components/Toolbar/SessionTimer/SessionTimerBtns.vue';
import FailedSessionMessageDialogs from '@/components/FailedSessionMessageDialogs.vue';

export default Vue.extend({
	name: 'Toolbar',
	mixins: [
		AwaitingServerUrlUi200Mixin,
	],
	props: {
		value: { type: Boolean, default: false },
		allowFileBrowserSidePanel: { type: Boolean, default: false },
	},
	data () {
		return {
			APP_BAR_HEIGHT,
		};
	},
	computed: {
		...mapState([
			'authUser',
			'isAdminProxyingUser',
			'user',
			'toolbar',
			'currSessions',
		]),
		...mapGetters([
			'userClubOfferBenefits',
		]),
		hasSessionRunning () {
			return this.currSessions.length;
		},
		isSessionLaunched () {
			return !!(
				this.toolbar.session &&
				READY_SESSION_STATES.includes(this.toolbar.session.state) &&
				this.$route.name === ROUTER.LAUNCH
			);
		},
		isSessionApiEnabled () {
			return !!(
				this.toolbar.session &&
				this.toolbar.session.apiEnabled &&
				this.toolbar.session.serverUrlUi
			);
		},
	},
	watch: {
		currSessions: {
			immediate: true,
			handler(newVal: Session[], oldVal: Session[]) {
				if (newVal !== oldVal) {
					this.syncToolbarSession(this.$route, newVal);
				}
			}
		},
		'$route': {
			immediate: true,
			handler(newVal: Route, oldVal: Route) {
				if (newVal !== oldVal) {
					this.syncToolbarSession(newVal, this.currSessions);
				}
			}
		},
		toolbar: {
			immediate: true,
			handler (newVal: ToolbarState, oldVal: ToolbarState) {
				const newSessionState: SessionState = newVal?.session?.state || null;
				const oldSessionState: SessionState = oldVal?.session?.state || null;

				/*
				Automatically collapse side nav
				*/
				if (
					READY_SESSION_STATES.includes(newSessionState) &&
					!READY_SESSION_STATES.includes(oldSessionState) &&
					!this.toolbar.runnitsSideNavCollapsed &&
					this.$route.name === ROUTER.LAUNCH
				) {
					this.updateToolbar({
						runnitsSideNavCollapsed: true,
					});
				}

				/*
				Automatically expand side nav
				*/
				if (
					STOPPING_SESSION_STATES.includes(newSessionState) &&
					READY_SESSION_STATES.includes(oldSessionState) &&
					this.toolbar.runnitsSideNavCollapsed
				) {
					this.updateToolbar({
						runnitsSideNavCollapsed: false,
					});
				}

				/*
				Play the session start sound only once
				*/
				if (
					READY_SESSION_STATES.includes(newSessionState) &&
					BOOTING_SESSION_STATES.includes(oldSessionState)
				) {
					this.$autoSoundMachine.playSessionStart(false);
					this.startAwaitingServerUrlUi200Loop(this.AWAITING_200);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateToolbar',
		]),
		onOpenSessionBar () {
			this.updateToolbar({
				sessionBarOpen: true,
			});
		},
		openMobileMenu () {
			this.updateToolbar({
				mobileMenuOpen: true,
			});
		},
		launchSession (session: Session) {
			if (session) {
				this.updateToolbar({
					session,
				});
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.LAUNCH,
					params: {
						sessionId: session.id,
					},
				});
			}
		},
		delaunchSession (routeName: string) {
			if (this.toolbar.session) {
				this.updateToolbar({
					session: null,
				});
				this.routerReplace(this.$route, this.$router, { name: routeName });
			}
		},
		syncToolbarSession (route: Route, currSessions: Session[]) {
			if (!currSessions || !currSessions.length) {
				this.delaunchSession(ROUTER.SESSIONS);
				return;
			}

			const preventLaunchSession: boolean = !!route.meta.preventLaunchSession;
			const isInternalAdminPage: boolean = !!route.meta.isInternalAdminPage;
			const isOnAnotherSessionPage: boolean = route.name === ROUTER.ANOTHER_SESSION;
			const isOnSetupAnotherSessionPage: boolean = route.name === ROUTER.SETUP_ANOTHER_SESSION;
			const isOnLaunchPage: boolean = route.name === ROUTER.LAUNCH;
			const awaitSessionUuid: string = this._get(route.query, 'awaitSession', null);
			const launchSessionId: string = this._get(route.params, 'sessionId', null);

			if (isOnAnotherSessionPage || isOnSetupAnotherSessionPage) {
				const foundSession: Session = awaitSessionUuid ? _find(currSessions, ['uuid', awaitSessionUuid]) : null;
				if (foundSession) this.launchSession(foundSession);
			} else if (isOnLaunchPage) {
				const foundSession: Session = launchSessionId ? _find(currSessions, ['id', launchSessionId]) : null;
				if (foundSession) this.launchSession(foundSession);
				else this.delaunchSession(ROUTER.ANOTHER_SESSION);
			} else {
				const foundSession: Session = launchSessionId ? _find(currSessions, ['id', launchSessionId]) : currSessions[0];
				if (
					foundSession &&
					!preventLaunchSession &&
					!isInternalAdminPage &&
					(
						route.name === ROUTER.SETUP_SESSION ||
						(
							route.name !== ROUTER.LAUNCH &&
							foundSession.state === SESSION_STATE.READY &&
							confirm('You already have a session running, would you like to go to it now?')
						)
					)
				) {
					this.launchSession(foundSession);
				}
			}
		},
	},
	components: {
		SessionTimer,
		FileBrowserBtn,
		SessionInfoCard,
		NotificationsBtn,
		SessionTimerBtns,
		AdminSessionStats,
		EnabledApiAnimation,
		FileBrowserNewTabBtn,
		FailedSessionMessageDialogs,
	},
});
